import localeDataset from '@wix/locale-dataset-javascript';

import { type MultilineAddressSubmission } from './types';

// Formatting copied from
// https://github.com/wix-private/address-formatting-evaluator/blob/master/packages/address-formatting-evaluator-client/src/utils.ts
type StreetAddress = {
  name?: string;
  number?: string;
  apt?: string;
};

export type TranslateCountry = (
  displayNameKey: string,
  language?: string,
) => string;

export interface FormatMultilineAddressOptions {
  translateCountry?: TranslateCountry;
  language?: string;
}

function formatStreetNumber(streetAddress: StreetAddress): string {
  const number = streetAddress?.number || '';
  const apt = streetAddress?.apt ? `-${streetAddress.apt}` : '';
  return number ? `${number}${apt}` : '';
}

function formatAddressLine(
  isStreetFormat: boolean,
  streetAddress: StreetAddress,
  addressLine?: string,
): string | undefined {
  if (!isStreetFormat) {
    return addressLine;
  }

  const streetNumber = formatStreetNumber(streetAddress);
  return `${streetAddress?.name || ''} ${streetNumber}`;
}

export function formatMultilineAddress(
  submission: MultilineAddressSubmission,
  options?: FormatMultilineAddressOptions,
): string {
  const country = submission.country
    ? localeDataset.getCountryByShortKey(submission.country)
    : undefined;
  const streetAddress: StreetAddress = {
    name: submission.streetName,
    number: submission.streetNumber,
    apt: (submission as any).apartment,
  };
  const isStreetFormat = Object.values(streetAddress).some(Boolean);

  const resolvedCountry =
    country && options?.translateCountry
      ? options.translateCountry(country.displayName, options.language)
      : submission.country;

  const formattedAddress = localeDataset.getFormattedAddress(
    {
      country: resolvedCountry,
      subdivision: submission.subdivision,
      city: submission.city,
      zipCode: submission.postalCode,
      addressLine1: formatAddressLine(
        isStreetFormat,
        streetAddress,
        submission.addressLine,
      ),
      addressLine2: submission.addressLine2,
    },
    country?.key,
  );
  return formattedAddress.replace(/^[\s,]+|[\s,]+$/g, '');
}
